import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useActiveProperty } from "@/context/ActivePropertyContext";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { BedDouble, MessagesSquare, RefreshCw, Send, CheckCircle2, X, LayoutGrid, ChevronDown, ChevronRight, Users, ClipboardCheck, Pencil, Trophy } from "lucide-react";
import { HkScorecard } from "@/components/HkScorecard";
import { HkRoomsReport } from "@/components/HkRoomsReport";
import { useAuth } from "@/context/AuthContext";
import { propColor } from "@/lib/propertyColors";
import { Link } from "react-router-dom";

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function fmtDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

const roomFloor = (number) => {
  const m = String(number || "").match(/^(\d+)/);
  if (!m) return "Other";
  const n = parseInt(m[1], 10);
  if (n >= 100) return String(Math.floor(n / 100));
  if (n >= 10) return String(Math.floor(n / 10));
  return "1";
};
const RDOT = { dirty: "bg-red-500", clean: "bg-blue-500", inspected: "bg-emerald-500", ooo: "bg-zinc-500" };
const RLABEL = { dirty: "Dirty", clean: "Clean / Ready", inspected: "Inspected", ooo: "Out of Order" };
const STATUS_OPTS = [
  { key: "dirty", label: "Dirty", hint: "Needs cleaning", cls: "bg-red-500 hover:bg-red-600" },
  { key: "clean", label: "Clean / Ready for Inspection", hint: "Housekeeper finished", cls: "bg-blue-500 hover:bg-blue-600" },
  { key: "inspected", label: "Inspected — Ready to Sell", hint: "Front desk can sell it", cls: "bg-emerald-500 hover:bg-emerald-600" },
  { key: "ooo", label: "Out of Order", hint: "Not sellable", cls: "bg-zinc-600 hover:bg-zinc-700" },
];

export default function Housekeeping() {
  const { user } = useAuth();
  const [props, setProps] = useState([]);
  const [assignments, setAssignments] = useState({}); // number -> {employee_id, employee_name}
  const { activePropertyId: propertyId, setActivePropertyId: setPropertyId } = useActiveProperty();
  const currentProp = props.find((p) => p.id === propertyId);
  const assignEnabled = !!currentProp?.hk_assignments_enabled;
  const [ready, setReady] = useState([]);
  const [ooo, setOoo] = useState([]);
  const [inspected, setInspected] = useState([]);

  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState("");
  const chatEndRef = useRef(null);

  const [allRooms, setAllRooms] = useState([]);
  const [collapsedFloors, setCollapsedFloors] = useState({});
  const [selRoom, setSelRoom] = useState(null);
  const [hkDate, setHkDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });
  const [roomLog, setRoomLog] = useState({}); // number -> {cleaned_by, room_type, inspected_by}
  const [hkStaff, setHkStaff] = useState([]);
  const [selHk, setSelHk] = useState(""); // chosen housekeeper id in dialog
  const [cleanedLog, setCleanedLog] = useState({ groups: [], grand_total: { stayover: 0, checkout: 0, total: 0 } });
  const [editRoom, setEditRoom] = useState(null); // {number, room_type, housekeeper_id}
  const toggleFloor = (f) => setCollapsedFloors((c) => ({ ...c, [f]: !c[f] }));

  const loadCleanedLog = useCallback(() => {
    if (!propertyId) return;
    api.get("/housekeeping/cleaned-log", { params: { property_id: propertyId, date: hkDate } })
      .then((r) => setCleanedLog(r.data || { groups: [], grand_total: { stayover: 0, checkout: 0, total: 0 } }))
      .catch(() => setCleanedLog({ groups: [], grand_total: { stayover: 0, checkout: 0, total: 0 } }));
  }, [propertyId, hkDate]);
  useEffect(() => { loadCleanedLog(); }, [loadCleanedLog]);

  const saveCleanedEdit = async (patch) => {
    try {
      await api.post("/housekeeping/cleaned-log/edit", { property_id: propertyId, date: hkDate, number: editRoom.number, ...patch });
      toast.success("Updated");
      setEditRoom(null);
      loadCleanedLog();
      loadAllRooms();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const loadAllRooms = useCallback(() => {
    if (!propertyId) return;
    api.get("/housekeeping/all-rooms", { params: { property_id: propertyId } })
      .then((r) => setAllRooms(r.data.rooms || [])).catch(() => setAllRooms([]));
    api.get("/housekeeping/room-log", { params: { property_id: propertyId, date: hkDate } })
      .then((r) => {
        const m = {};
        (r.data.rooms || []).forEach((x) => { m[x.number] = x; });
        setRoomLog(m);
      }).catch(() => setRoomLog({}));
  }, [propertyId, hkDate]);
  useEffect(() => { loadAllRooms(); }, [loadAllRooms]);

  const loadAssignments = useCallback(() => {
    if (!propertyId || !assignEnabled) return;
    api.get("/housekeeping/assignments", { params: { property_id: propertyId, date: hkDate } })
      .then((r) => setAssignments(r.data.assignments || {})).catch(() => setAssignments({}));
  }, [propertyId, hkDate, assignEnabled]);
  useEffect(() => { loadAssignments(); }, [loadAssignments]);

  const assignRoom = async (number, employee_id) => {
    try {
      await api.post("/housekeeping/assign", { property_id: propertyId, date: hkDate, number, employee_id: employee_id || null });
      loadAssignments();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  useEffect(() => {
    if (!propertyId) return;
    api.get("/employees", { params: { property_id: propertyId } })
      .then((r) => setHkStaff((r.data || []).filter((e) => {
        const depts = [e.department, ...((e.positions || []).map((p) => p.department))].map((d) => (d || "").toLowerCase());
        return depts.some((d) => ["housekeeping", "houseman", "laundry", "executive"].includes(d));
      }))).catch(() => setHkStaff([]));
  }, [propertyId]);

  const setRoomStatus = async (number, status) => {
    try {
      await api.post("/housekeeping/status", { property_id: propertyId, number, status, date: hkDate, cleaned_by_id: selHk || null });
      setSelRoom(null);
      setSelHk("");
      loadAllRooms();
      loadRooms();
      window.dispatchEvent(new Event("comms-refresh"));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  useEffect(() => {
    api.get("/properties").then((r) => {
      setProps(r.data);
      if (!propertyId && r.data[0]) setPropertyId(r.data[0].id);
    }).catch(() => {});
  }, []);

  const loadRooms = useCallback(() => {
    if (!propertyId) return;
    api.get("/housekeeping/rooms", { params: { property_id: propertyId } })
      .then((r) => { setReady(r.data.ready || []); setOoo(r.data.ooo || []); setInspected(r.data.inspected_today || []); })
      .catch(() => { setReady([]); setOoo([]); setInspected([]); });
  }, [propertyId]);

  useEffect(() => {
    loadRooms();
    const t = setInterval(loadRooms, 15000);
    return () => clearInterval(t);
  }, [loadRooms]);

  const loadChat = useCallback(() => {
    if (!propertyId) return;
    api.get("/chat/messages", { params: { property_id: propertyId, channel: "housekeeping" } })
      .then((r) => setMessages(r.data)).catch(() => setMessages([]));
  }, [propertyId]);

  useEffect(() => {
    loadChat();
    const t = setInterval(loadChat, 5000);
    return () => clearInterval(t);
  }, [loadChat]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const setStatus = async (roomId, status) => {
    try { await api.patch(`/housekeeping/rooms/${roomId}`, { status }); loadRooms(); window.dispatchEvent(new Event("comms-refresh")); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const removeRoom = async (roomId) => {
    try { await api.delete(`/housekeeping/rooms/${roomId}`); loadRooms(); window.dispatchEvent(new Event("comms-refresh")); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveNote = async (roomId, notes) => {
    try { await api.patch(`/housekeeping/rooms/${roomId}`, { notes }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const sendMsg = async () => {
    if (!msg.trim()) return;
    try {
      await api.post("/chat/messages", { property_id: propertyId, text: msg.trim(), channel: "housekeeping" });
      setMsg(""); loadChat();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const canScore = ["admin", "regional", "gm", "finance", "hr"].includes(user?.role);
  const canInspect = ["admin", "regional", "gm", "hr", "employee", "front_desk"].includes(user?.role);

  return (
    <div className="space-y-6" data-testid="housekeeping-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Housekeeping</h1>
          <p className="text-sm text-muted-foreground">Rooms cleaned by housekeepers appear here for inspection</p>
        </div>
        <div>
          <Label className="label-caps text-[0.6rem] text-muted-foreground">Property</Label>
          <Select value={propertyId} onValueChange={setPropertyId}>
            <SelectTrigger className="w-56" data-testid="hk-property-select"><SelectValue placeholder="Property" /></SelectTrigger>
            <SelectContent>{props.map((p) => <SelectItem key={p.id} value={p.id}><span className="inline-flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: propColor(p) }} />{p.name}</span></SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="board">
        <TabsList>
          <TabsTrigger value="board" data-testid="tab-board"><BedDouble className="mr-2 h-4 w-4" /> Rooms Board</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all-rooms"><LayoutGrid className="mr-2 h-4 w-4" /> All Rooms</TabsTrigger>
          <TabsTrigger value="cleaned" data-testid="tab-cleaned-log"><ClipboardCheck className="mr-2 h-4 w-4" /> Cleaned Log</TabsTrigger>
          {assignEnabled && <TabsTrigger value="assign" data-testid="tab-assign"><Users className="mr-2 h-4 w-4" /> Assignment</TabsTrigger>}
          {canScore && <TabsTrigger value="scorecard" data-testid="tab-hk-scorecard"><Trophy className="mr-2 h-4 w-4" /> Scorecard</TabsTrigger>}
          {canScore && <TabsTrigger value="rooms-report" data-testid="tab-hk-rooms-report"><ClipboardCheck className="mr-2 h-4 w-4" /> Rooms Report</TabsTrigger>}
          <TabsTrigger value="chat" data-testid="tab-hk-chat"><MessagesSquare className="mr-2 h-4 w-4" /> Housekeeping Chat</TabsTrigger>
        </TabsList>

        {canScore && (
          <TabsContent value="rooms-report" className="mt-4">
            <HkRoomsReport propertyId={propertyId} allowAll={["admin", "regional", "finance", "hr"].includes(user?.role)} />
          </TabsContent>
        )}
        {canScore && (
          <TabsContent value="scorecard" className="mt-4">
            <HkScorecard propertyId={propertyId} propertyName={currentProp?.name} />
          </TabsContent>
        )}

        {/* ---------- Board ---------- */}
        <TabsContent value="board" className="mt-4 space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-blue-500" /> Ready for Inspection <b data-testid="hk-count-ready">{ready.length}</b></span>
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-zinc-500" /> Out of Order <b>{ooo.length}</b></span>
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> Inspected Today <b>{inspected.length}</b></span>
            </div>
            <Button variant="outline" size="sm" onClick={loadRooms} data-testid="hk-refresh"><RefreshCw className="mr-2 h-4 w-4" /> Refresh</Button>
          </div>

          {/* Ready for Inspection */}
          <div>
            <div className="mb-3 flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-blue-500" />
              <h2 className="text-base font-bold">Ready for Inspection</h2>
              <span className="text-xs text-muted-foreground">— housekeeper marked these clean; exec confirms</span>
            </div>
            {ready.length === 0 ? (
              <p className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="hk-ready-empty">
                Nothing waiting. When a housekeeper marks a room cleaned from their phone, it shows up here.
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {ready.map((r) => (
                  <div key={r.id} className="flex flex-col gap-2 rounded-lg border border-blue-500/40 bg-blue-500/10 p-3" data-testid={`hk-ready-${r.number}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="text-xl font-black tracking-tight">Room {r.number}</div>
                          {r.room_type && (
                            <span data-testid={`hk-roomtype-${r.number}`}
                              className={`rounded-full px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wide ${r.room_type === "checkout" ? "bg-indigo-500/20 text-indigo-600 dark:text-indigo-300" : "bg-blue-500/20 text-blue-600 dark:text-blue-300"}`}>
                              {r.room_type === "checkout" ? "Checkout" : "Stayover"}
                            </span>
                          )}
                        </div>
                        <div className="truncate text-xs text-muted-foreground" data-testid={`hk-cleanedby-${r.number}`}>Cleaned by {r.cleaned_by || r.updated_by || "—"} · {fmtDateTime(r.updated_at)}</div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        {canInspect && (
                          <Button size="sm" onClick={() => setStatus(r.id, "inspected")} data-testid={`hk-inspect-${r.number}`}
                            className="bg-emerald-500 text-white hover:bg-emerald-600">
                            <CheckCircle2 className="mr-1.5 h-4 w-4" /> Inspected
                          </Button>
                        )}
                        <button onClick={() => removeRoom(r.id)} title="Remove" data-testid={`hk-remove-${r.number}`}
                          className="rounded p-1.5 text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button>
                      </div>
                    </div>
                    <Textarea rows={2} defaultValue={r.notes || ""} placeholder="Comment for this room (housekeeper sees this on their phone)…"
                      onBlur={(ev) => ev.target.value !== (r.notes || "") && saveNote(r.id, ev.target.value)}
                      data-testid={`hk-note-${r.number}`} className="min-h-0 resize-none bg-background/60 text-xs" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Out of Order */}
          {ooo.length > 0 && (
            <div>
              <div className="mb-3 flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-zinc-500" />
                <h2 className="text-base font-bold">Out of Order</h2>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {ooo.map((r) => (
                  <div key={r.id} className="flex items-center justify-between rounded-lg border border-zinc-500/40 bg-zinc-500/10 p-3" data-testid={`hk-ooo-${r.number}`}>
                    <div className="min-w-0">
                      <div className="text-xl font-black tracking-tight">Room {r.number}</div>
                      <div className="truncate text-xs text-muted-foreground">Flagged by {r.updated_by || "—"} · {fmtWhen(r.updated_at)}</div>
                    </div>
                    {canInspect && <Button size="sm" variant="outline" onClick={() => removeRoom(r.id)} data-testid={`hk-ooo-clear-${r.number}`}>Back to service</Button>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Inspected Today */}
          <div>
            <div className="mb-3 flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
              <h2 className="text-base font-bold">Inspected Today</h2>
              <span className="text-xs text-muted-foreground">— ready to sell (front desk)</span>
            </div>
            {inspected.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="hk-inspected-empty">No rooms inspected yet today.</p>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="hk-inspected-list">
                {inspected.map((r) => (
                  <span key={r.id} className="flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-sm font-semibold text-emerald-700 dark:text-emerald-400" data-testid={`hk-inspected-${r.number}`}>
                    <CheckCircle2 className="h-3.5 w-3.5" /> {r.number}
                    <span className="text-[0.65rem] font-normal text-muted-foreground">{fmtTime(r.updated_at)}</span>
                  </span>
                ))}
              </div>
            )}
          </div>

          <p className="text-xs text-muted-foreground">
            Set each property's room numbers on the <Link to="/properties" className="underline">Properties</Link> page. Housekeepers mark rooms cleaned from their phone via the Time Clock QR — no login needed.
          </p>
        </TabsContent>

        {/* ---------- All Rooms ---------- */}
        <TabsContent value="all" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              Tap any room to change its status or assign a housekeeper. Showing history for the selected date.
            </p>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">Date</Label>
              <Input type="date" value={hkDate} onChange={(e) => setHkDate(e.target.value)} className="h-9 w-40" data-testid="hk-date-picker" />
              <Button variant="outline" size="sm" onClick={loadAllRooms} data-testid="hk-all-refresh"><RefreshCw className="mr-2 h-4 w-4" /> Refresh</Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 text-xs">
            {STATUS_OPTS.map((s) => (
              <span key={s.key} className="flex items-center gap-1.5"><span className={`h-2.5 w-2.5 rounded-full ${RDOT[s.key]}`} /> {RLABEL[s.key]}</span>
            ))}
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full border border-muted-foreground/40" /> Not set</span>
          </div>
          {allRooms.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="hk-all-empty">
              No rooms configured. Add room numbers on the <Link to="/properties" className="underline">Properties</Link> page.
            </p>
          ) : (
            <div className="space-y-3">
              {Object.entries(allRooms.reduce((acc, r) => {
                const f = r.floor || roomFloor(r.number);
                (acc[f] = acc[f] || []).push(r);
                return acc;
              }, {}))
                .sort((a, b) => (parseInt(a[0], 10) || 999) - (parseInt(b[0], 10) || 999))
                .map(([floor, frooms]) => {
                  const collapsed = collapsedFloors[floor];
                  const todo = frooms.filter((r) => r.status !== "inspected").length;
                  return (
                    <div key={floor} className="rounded-lg border border-border" data-testid={`hk-floor-${floor}`}>
                      <button onClick={() => toggleFloor(floor)} className="flex w-full items-center justify-between px-4 py-3 text-left">
                        <span className="flex items-center gap-2 font-bold">
                          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          {floor === "Other" ? "Other rooms" : `Floor ${floor}`}
                          <span className="text-xs font-normal text-muted-foreground">({frooms.length} rooms)</span>
                        </span>
                        {todo > 0 && <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[0.65rem] font-semibold text-amber-600 dark:text-amber-400">{todo} to do</span>}
                      </button>
                      {!collapsed && (
                        <div className="grid grid-cols-2 gap-2 border-t border-border p-3 sm:grid-cols-4 lg:grid-cols-6">
                          {frooms.map((r) => {
                            const lg = roomLog[r.number] || {};
                            const onDate = r.cleaned_date === hkDate;
                            const cleanedBy = lg.cleaned_by || (onDate ? r.cleaned_by : "") || "";
                            const roomType = lg.room_type || (onDate ? r.room_type : "") || "";
                            const inspectedBy = lg.inspected_by || (r.inspected_date === hkDate ? r.inspected_by : "") || "";
                            return (
                            <button key={r.number} onClick={() => { if (canInspect) { setSelHk(assignments[r.number]?.employee_id || ""); setSelRoom(r); } }} disabled={!canInspect}
                              data-testid={`hk-allroom-${r.number}`}
                              className={`flex flex-col items-center rounded-lg border p-2 text-center transition-colors ${r.notes ? "border-amber-500/60 bg-amber-500/10" : "border-border bg-secondary/40"} ${canInspect ? "hover:border-primary" : "cursor-default opacity-80"}`}>
                              <span className="text-base font-bold">{r.number}</span>
                              <span className="mt-1 flex items-center gap-1 text-[0.6rem] text-muted-foreground">
                                <span className={`h-2 w-2 rounded-full ${RDOT[r.status] || "border border-muted-foreground/40"}`} />
                                {RLABEL[r.status] || "Not set"}
                              </span>
                              {cleanedBy && (
                                <span className="mt-1 text-[0.6rem] text-blue-600 dark:text-blue-400" data-testid={`hk-cleanedby-${r.number}`}>
                                  🧹 {cleanedBy}{roomType ? ` · ${roomType === "checkout" ? "C/O" : "Stay"}` : ""}
                                </span>
                              )}
                              {inspectedBy && (
                                <span className="text-[0.6rem] text-emerald-600 dark:text-emerald-400" data-testid={`hk-inspectedby-${r.number}`}>✓ Insp: {inspectedBy}</span>
                              )}
                            </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </TabsContent>

        {/* ---------- Cleaned Log (grouped by housekeeper) ---------- */}
        <TabsContent value="cleaned" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">Which rooms each housekeeper cleaned on the selected day. Tap a room to correct the housekeeper or stayover/checkout.</p>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">Date</Label>
              <Input type="date" value={hkDate} onChange={(e) => setHkDate(e.target.value)} className="h-9 w-40" data-testid="hk-cleaned-date-picker" />
              <Button variant="outline" size="sm" onClick={loadCleanedLog} data-testid="hk-cleaned-refresh"><RefreshCw className="mr-2 h-4 w-4" /> Refresh</Button>
            </div>
          </div>
          {cleanedLog.groups.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="hk-cleaned-empty">
              No rooms were logged as cleaned on this date.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap gap-4 rounded-lg border border-border bg-secondary/40 p-3 text-sm" data-testid="hk-cleaned-grand">
                <span className="font-bold">Total: {cleanedLog.grand_total.total} rooms</span>
                <span className="text-blue-600 dark:text-blue-400">{cleanedLog.grand_total.stayover} stayover</span>
                <span className="text-amber-600 dark:text-amber-400">{cleanedLog.grand_total.checkout} checkout</span>
              </div>
              <div className="space-y-3">
                {cleanedLog.groups.map((g) => (
                  <div key={g.housekeeper_id || g.housekeeper} className="rounded-lg border border-border" data-testid={`hk-cleaned-group-${g.housekeeper_id || "unassigned"}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
                      <span className="font-bold">{g.housekeeper}</span>
                      <span className="text-xs text-muted-foreground">
                        {g.total} rooms · <span className="text-blue-600 dark:text-blue-400">{g.stayover} stay</span> · <span className="text-amber-600 dark:text-amber-400">{g.checkout} C/O</span>
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 p-3 sm:grid-cols-4 lg:grid-cols-6">
                      {g.rooms.map((r) => (
                        <button key={r.number} onClick={() => { if (canInspect) setEditRoom({ number: r.number, room_type: r.room_type || "stayover", housekeeper_id: g.housekeeper_id || "" }); }}
                          disabled={!canInspect} data-testid={`hk-cleaned-room-${r.number}`}
                          className={`flex flex-col items-center rounded-lg border p-2 text-center transition-colors ${r.room_type === "checkout" ? "border-amber-500/50 bg-amber-500/10" : "border-blue-500/40 bg-blue-500/10"} ${canInspect ? "hover:border-primary" : "cursor-default opacity-80"}`}>
                          <span className="flex items-center gap-1 text-base font-bold">{r.number} {canInspect && <Pencil className="h-3 w-3 text-muted-foreground" />}</span>
                          <span className="text-[0.6rem] text-muted-foreground">{r.room_type === "checkout" ? "Checkout" : "Stayover"}</span>
                          {r.inspected_by && <span className="text-[0.6rem] text-emerald-600 dark:text-emerald-400">✓ {r.inspected_by}</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </TabsContent>


        {/* ---------- Assignment (optional, GM-enabled) ---------- */}
        {assignEnabled && (
          <TabsContent value="assign" className="mt-4 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">Assign each room to a housekeeper for the selected day. Optional — leave blank to keep the current workflow.</p>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground">Date</Label>
                <Input type="date" value={hkDate} onChange={(e) => setHkDate(e.target.value)} className="h-9 w-40" data-testid="assign-date-picker" />
              </div>
            </div>
            {allRooms.length === 0 ? (
              <p className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No rooms configured. Add room numbers on the Properties page.</p>
            ) : (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {allRooms.map((r) => (
                  <div key={r.number} className="flex items-center gap-2 rounded-lg border border-border p-2" data-testid={`assign-row-${r.number}`}>
                    <span className="w-14 font-bold">{r.number}</span>
                    <Select value={assignments[r.number]?.employee_id || "none"} onValueChange={(v) => assignRoom(r.number, v === "none" ? "" : v)}>
                      <SelectTrigger className="h-8" data-testid={`assign-select-${r.number}`}><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Unassigned</SelectItem>
                        {hkStaff.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        )}

        {/* ---------- Chat ---------- */}
        <TabsContent value="chat" className="mt-4">
          <div className="flex h-[60vh] flex-col rounded-lg border border-border">
            <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="hk-chat-list">
              {messages.length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">No messages yet. Coordinate room readiness, priorities and issues here.</p>
              ) : messages.map((m) => {
                const mine = m.user_id === String(user?.id);
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-lg px-3 py-2 ${mine ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
                      {!mine && <div className="mb-0.5 text-[0.65rem] font-semibold opacity-70">{m.user_name}</div>}
                      <div className="text-sm">{m.text}</div>
                      <div className="mt-0.5 text-[0.6rem] opacity-60">{fmtWhen(m.created_at)}</div>
                    </div>
                  </div>
                );
              })}
              <div ref={chatEndRef} />
            </div>
            <div className="flex gap-2 border-t border-border p-3">
              <Input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMsg()} placeholder="Message housekeeping team…" data-testid="hk-chat-input" />
              <Button onClick={sendMsg} data-testid="hk-chat-send"><Send className="h-4 w-4" /></Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!selRoom} onOpenChange={(o) => !o && setSelRoom(null)}>
        <DialogContent className="max-w-sm" data-testid="hk-status-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Room {selRoom?.number}</DialogTitle>
            <DialogDescription>
              Set status for {hkDate}.{selRoom?.status ? ` Currently ${RLABEL[selRoom.status] || selRoom.status}.` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            {hkStaff.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-xs">Housekeeper (for Clean / Inspected — optional)</Label>
                <Select value={selHk || "none"} onValueChange={(v) => setSelHk(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="hk-assign-select"><SelectValue placeholder="Not specified" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not specified</SelectItem>
                    {hkStaff.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {STATUS_OPTS.map((s) => (
              <button key={s.key} onClick={() => setRoomStatus(selRoom.number, s.key)}
                data-testid={`hk-set-${s.key}`}
                className={`flex w-full items-center justify-between rounded-lg px-4 py-3 text-left font-semibold text-white ${s.cls} ${selRoom?.status === s.key ? "ring-2 ring-offset-2 ring-offset-background" : ""}`}>
                <span>{s.label}</span>
                <span className="text-xs font-normal opacity-90">{s.hint}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Cleaned Log edit dialog */}
      <Dialog open={!!editRoom} onOpenChange={(o) => !o && setEditRoom(null)}>
        <DialogContent className="max-w-sm" data-testid="hk-cleaned-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Room {editRoom?.number}</DialogTitle>
            <DialogDescription>Correct who cleaned it and stayover/checkout for {hkDate}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Cleaned by</Label>
              <Select value={editRoom?.housekeeper_id || "none"} onValueChange={(v) => setEditRoom((s) => ({ ...s, housekeeper_id: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="hk-cleaned-edit-hk"><SelectValue placeholder="Select housekeeper" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {hkStaff.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Room type</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button type="button" variant={editRoom?.room_type === "stayover" ? "default" : "outline"} onClick={() => setEditRoom((s) => ({ ...s, room_type: "stayover" }))} data-testid="hk-cleaned-edit-stayover">Stayover</Button>
                <Button type="button" variant={editRoom?.room_type === "checkout" ? "default" : "outline"} onClick={() => setEditRoom((s) => ({ ...s, room_type: "checkout" }))} data-testid="hk-cleaned-edit-checkout">Checkout</Button>
              </div>
            </div>
            <Button className="w-full" onClick={() => saveCleanedEdit({ cleaned_by_id: editRoom.housekeeper_id, room_type: editRoom.room_type })} data-testid="hk-cleaned-edit-save">Save correction</Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
